import { Dispatch, SetStateAction, useState } from "react";

import { useCallbackRef } from "./use-callback-ref";

interface UseControllableStateProps<T> {
  value?: T;
  defaultValue?: T | (() => T);
  onChange?: (value: T) => void;
  shouldUpdate?: (prev: T, next: T) => boolean;
}

/**
 * The `useControllableState` hook provides a state value and a function to update it, similar to React's `useState`.
 * This hook is useful for components that can operate in both controlled and uncontrolled modes, allowing for
 * external control over the component's internal state when necessary.
 *
 * Example: Toggle Switch Component
 * A toggle switch can be controlled externally or manage its own state. If the `isChecked` prop is not provided,
 * the switch handles its own checked state.
 *
 * ```jsx
 * import React from "react";
 * import { useControllableState } from "use-overridable-state";
 *
 * interface ToggleSwitchProps {
 *   isChecked?: boolean;
 *   onToggle?: (checked: boolean) => void;
 * }
 *
 * export const ToggleSwitch: React.FC<ToggleSwitchProps> = ({ isChecked, onToggle }) => {
 *   const [checked, setChecked] = useControllableState({
 *     value: isChecked,
 *     defaultValue: false,
 *     onChange: onToggle,
 *   });
 *
 *   const handleToggle = () => {
 *     setChecked((prev) => !prev);
 *   };
 *
 *   return (
 *     <label>
 *       <input type="checkbox" checked={checked} onChange={handleToggle} />
 *       Toggle Switch
 *     </label>
 *   );
 * };
 * ```
 *
 * To let the toggle switch manage its own state, omit the `isChecked` prop:
 * ```jsx
 * <ToggleSwitch />
 * ```
 *
 * To control the toggle switch's state from a parent component, provide the `isChecked` and `onToggle` props:
 * ```jsx
 * const [isChecked, setIsChecked] = useState(false);
 *
 * const handleToggle = (checked: boolean) => {
 *   setIsChecked(checked);
 * };
 *
 * <ToggleSwitch isChecked={isChecked} onToggle={handleToggle} />
 * ```
 */
export function useControllableState<T = boolean>(
  props: UseControllableStateProps<T>,
) {
  const {
    value: valueProp,
    defaultValue,
    onChange,
    shouldUpdate = (prev, next) => prev !== next,
  } = props;

  const onChangeProp = useCallbackRef(onChange);
  const shouldUpdateProp = useCallbackRef(shouldUpdate);

  const [uncontrolledState, setUncontrolledState] = useState(defaultValue as T);
  const controlled = valueProp !== undefined;
  const value = controlled ? valueProp : uncontrolledState;

  const setValue = useCallbackRef(
    (next: SetStateAction<T>) => {
      const setter = next as (prevState?: T) => T;
      const nextValue = typeof next === "function" ? setter(value) : next;

      if (!shouldUpdateProp(value, nextValue)) {
        return;
      }

      if (!controlled) {
        setUncontrolledState(nextValue);
      }

      onChangeProp(nextValue);
    },
    [controlled, onChangeProp, value, shouldUpdateProp],
  );

  return [value, setValue] as [T, Dispatch<SetStateAction<T>>];
}
